import React, { useEffect, useMemo, useState } from 'react';
import {
  collection, doc, getDocs, onSnapshot, runTransaction, serverTimestamp, setDoc,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { AccessDenied, canAccess } from '../adminAccess';
import {
  APPROVED_CITY_NAMES,
  approvedCemeteryCount,
} from '../approvedCemeteryCatalog';
import {
  cemeteriesForSelectedCity,
  normalizeCatalogName,
  newCemeteryDraft,
  parseOptionalCoordinate,
  preferredInitialCityId,
  stableCemeteryId,
  stableCityId,
} from '../cemeteryAdminUtils';
import { buildCatalogSync, referenceCount, nameOf } from '../catalogSync';
import { DateTime } from '../components/AdminUI';
import { safeGoogleMapsUrl } from '../orderLocation';

export default function CemeteriesPage({ user }) {
  const [liveCities, setLiveCities] = useState([]);
  const [liveCemeteries, setLiveCemeteries] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [mapFilter, setMapFilter] = useState('all');
  const [cityEditor, setCityEditor] = useState(null);
  const [cemeteryEditor, setCemeteryEditor] = useState(null);
  const [notice, setNotice] = useState('');
  const [importPreview, setImportPreview] = useState(null);

  useEffect(() => {
    const stopCities = onSnapshot(collection(db, 'cities'), snapshot =>
      setLiveCities(snapshot.docs.map(item => ({ id: item.id, ...item.data() }))), () => setNotice('Unable to load cities. Please refresh or sign in again.'));
    const stopCemeteries = onSnapshot(collection(db, 'cemeteries'), snapshot =>
      setLiveCemeteries(snapshot.docs.map(item => ({ id: item.id, ...item.data() }))), () => setNotice('Unable to load cemeteries. Please refresh or sign in again.'));
    return () => { stopCities(); stopCemeteries(); };
  }, []);

  const cities = useMemo(() => liveCities.map(c => ({ ...c, name: nameOf(c) })).sort((a, b) => a.name.localeCompare(b.name, 'az')), [liveCities]);
  const cemeteries = useMemo(
    () => liveCemeteries.map(c => ({ ...c, name: nameOf(c) })),
    [liveCemeteries],
  );
  useEffect(() => {
    if (!selectedCityId && cities.length) {
      setSelectedCityId(preferredInitialCityId(cities));
    }
  }, [cities, selectedCityId]);
  const selectedCity = cities.find(city => city.id === selectedCityId);
  const displayed = cemeteriesForSelectedCity(cemeteries, selectedCityId,
    normalizeCatalogName(selectedCity?.name).includes(normalizeCatalogName(search)) ? '' : search)
    .filter(c => statusFilter === 'all' || (c.isActive !== false ? 'active' : 'inactive') === statusFilter)
    .filter(c => mapFilter === 'all' || (hasMap(c) ? 'available' : 'soon') === mapFilter);
  const visibleCities = cities.filter(city =>
    normalizeCatalogName(city.name).includes(normalizeCatalogName(search)) ||
    cemeteries.some(cemetery => cemetery.cityId === city.id &&
      normalizeCatalogName(cemetery.name).includes(normalizeCatalogName(search))));

  if (!canAccess(user, 'cemeteries')) return <AccessDenied />;

  function countFor(city) {
    return cemeteries.filter(item => item.cityId === city.id && !item.deletedAt).length;
  }

  async function prepareImportPreview() {
    try {
      const snapshot = await readCatalogSnapshot();
      setImportPreview({ ...buildCatalogSync(snapshot.cities, snapshot.cemeteries, snapshot.references), snapshot });
    } catch (_) { setNotice('Cannot inspect catalog references. Synchronization is blocked until authorized read access is available.'); }
  }

  async function importMissingApprovedRecords() {
    if (!importPreview || !window.confirm(`Synchronize the final catalog: add ${importPreview.addedCities} cities and ${importPreview.addedCemeteries} cemeteries; deactivate ${importPreview.deactivated} extra/duplicate records? Historical orders and all map assets are preserved.`)) return;
    try {
      const fresh = await readCatalogSnapshot();
      const plan = buildCatalogSync(fresh.cities, fresh.cemeteries, fresh.references);
      if (JSON.stringify(plan) !== JSON.stringify(buildCatalogSync(importPreview.snapshot.cities, importPreview.snapshot.cemeteries, importPreview.snapshot.references))) {
        setImportPreview({ ...plan, snapshot: fresh });
        setNotice('Catalog or references changed. Review the refreshed preview and confirm again.');
        return;
      }
      await runTransaction(db, async tx => {
        const targets = plan.writes.map(w => doc(db, w.collection, w.id));
        const current = await Promise.all(targets.map(target => tx.get(target)));
        current.forEach((snapshot, index) => {
          const write = plan.writes[index];
          const previous = fresh[write.collection].find(r => r.id === write.id);
          if (snapshot.exists() !== !!previous || (previous && JSON.stringify(snapshot.data()) !== JSON.stringify(previous.data))) {
            throw new Error('Catalog changed; refresh the preview.');
          }
        });
        plan.writes.forEach((write, index) => tx.set(targets[index], {
          ...write.data, updatedAt: serverTimestamp(),
          ...(!write.exists ? { createdAt: serverTimestamp() } : {}),
        }, { merge: true }));
      });
      setNotice(`Catalog synchronized: ${plan.kept} cemeteries kept, ${plan.addedCemeteries} added, ${plan.deactivated} extra city/cemetery records deactivated.`);
      setImportPreview(null);
    } catch (error) { setNotice(error.message || 'Synchronization failed. Refresh and retry.'); }
  }

  async function toggleRecord(collectionName, record) {
    try {
      const active = record.isActive === false;
      if (!active && !(await confirmDeactivation(collectionName, record))) return;
      await setDoc(doc(db, collectionName, record.id), { isActive: active, updatedAt: serverTimestamp() }, { merge: true });
    } catch (_) { setNotice('Unable to inspect references or update this record. No deletion was performed.'); }
  }

  async function removeCity(city) { await toggleRecord('cities', city); }
  async function softDeleteCemetery(cemetery) {
    if (cemetery.isActive === false) { setNotice('Already inactive. Historical records remain available.'); return; }
    await toggleRecord('cemeteries', cemetery);
  }

  return (
    <div className="page-shell">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="page-title">Cities &amp; Cemeteries</h1>
          <p className="text-sm text-gray-500">Manage cities and their cemeteries.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-secondary" onClick={() => setCityEditor({})}>+ Add City</button>
          <button className="btn-primary" disabled={!selectedCityId} onClick={() => setCemeteryEditor(newCemeteryDraft(selectedCityId))}>+ Add Cemetery</button>
          <button className="btn-secondary" onClick={prepareImportPreview}>Preview approved import</button>
        </div>
      </div>

      {notice && <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{notice}</div>}
      {importPreview && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          Dry run: {importPreview.kept} cemeteries kept; {importPreview.addedCities} cities and {importPreview.addedCemeteries} cemeteries to add; {importPreview.deactivated} extra/duplicate records to deactivate.
          <p>{importPreview.extras.filter(r => r.references > 0).length} extra records have historical references and will be preserved.</p>
          <div className="mt-3 flex gap-2">
            <button className="btn-primary" onClick={importMissingApprovedRecords}>Confirm final catalog sync</button>
            <button className="btn-secondary" onClick={() => setImportPreview(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <label>Status <select className="input-field ml-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}><option value="all">All</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
        <label>2D Map <select className="input-field ml-2" value={mapFilter} onChange={e => setMapFilter(e.target.value)}><option value="all">All</option><option value="available">Available</option><option value="soon">Coming Soon</option></select></label>
      </div>
      <input aria-label="Search city or cemetery" className="input-field w-full mb-4" placeholder="Search city or cemetery..." value={search} onChange={event => setSearch(event.target.value)} />

      <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
        <aside className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm max-h-[70vh] overflow-y-auto">
          {visibleCities.map(city => (
            <div key={city.id} className={`mt-1 rounded-xl p-2 ${selectedCityId === city.id ? 'bg-primary/10' : 'hover:bg-gray-50'}`}>
              <button className="w-full text-left" onClick={() => setSelectedCityId(city.id)}>
                <span className="block font-medium text-gray-900">{city.name}</span>
                <span className="text-xs text-gray-500">{countFor(city)} cemeteries · {city.isActive === false ? 'Inactive' : 'Active'}</span>
              </button>
              <div className="text-xs text-gray-500"><DateTime value={city.updatedAt} /></div>
              <div className="mt-1 flex flex-wrap gap-2 text-xs">
                <button className="text-blue-600" onClick={() => setCityEditor(city)}>Edit</button>
                <button className="text-red-600" onClick={() => removeCity(city)}>{city.isActive === false ? 'Activate' : 'Deactivate'}</button>
              </div>
            </div>
          ))}
        </aside>

        <section>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">{selectedCity ? `${selectedCity.name} Cemeteries` : 'Select a city'}</h2>
              <p className="text-sm text-gray-500">{displayed.length} cemeteries</p>
            </div>
            <span className="text-xs text-gray-500">Official baseline: {APPROVED_CITY_NAMES.length} cities / {approvedCemeteryCount} cemeteries + Səbiskar exception</span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {displayed.map(cemetery => (
              <CemeteryCard key={cemetery.id} cemetery={cemetery}
                cityName={cities.find(city => city.id === cemetery.cityId)?.name || cemetery.cityName || '—'}
                onEdit={() => setCemeteryEditor(cemetery)}
                onToggle={() => toggleRecord('cemeteries', cemetery)}
                onDelete={() => softDeleteCemetery(cemetery)} />
            ))}
          </div>
          {selectedCity && displayed.length === 0 && !search && (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-14 text-center">
              <p className="text-gray-600">No cemeteries added for {selectedCity.name} yet.</p>
              <button className="btn-primary mt-4" onClick={() => setCemeteryEditor(newCemeteryDraft(selectedCity.id))}>+ Add Cemetery</button>
            </div>
          )}
          {selectedCity && displayed.length === 0 && search && (
            <p className="rounded-2xl border border-gray-100 bg-white px-6 py-12 text-center text-gray-500">No matching cemeteries in {selectedCity.name}.</p>
          )}
        </section>
      </div>

      {cityEditor && <CityModal city={cityEditor} onClose={() => setCityEditor(null)} onSaved={(message, cityId) => { setNotice(message); setSelectedCityId(cityId); }} />}
      {cemeteryEditor && <CemeteryModal cemetery={cemeteryEditor} cities={cities} onClose={() => setCemeteryEditor(null)} onSaved={setNotice} />}
    </div>
  );
}

function CemeteryCard({ cemetery, cityName, onEdit, onToggle, onDelete }) {
  return (
    <article className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${cemetery.isActive === false ? 'opacity-60' : ''}`}>
      {cemetery.imageUrl
        ? <img src={cemetery.imageUrl} alt={cemetery.name} className="h-36 w-full object-cover" />
        : <div className="flex h-36 items-center justify-center bg-primary/10 text-4xl">🕌</div>}
      <div className="p-4">
        <div className="flex justify-between gap-2">
          <div><h3 className="font-semibold text-gray-900">{cemetery.name}</h3><p className="text-xs text-gray-500">{cityName}</p></div>
          <span className={`h-fit rounded-full px-2 py-1 text-xs ${cemetery.isActive === false ? 'bg-gray-100' : 'bg-green-100 text-green-700'}`}>{cemetery.isActive === false ? 'Inactive' : 'Active'}</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-1 text-xs">
          {cemetery.specialCategory && <Badge>Special</Badge>}
          {cemetery.requiresManualConfirmation && <Badge>Manual confirmation</Badge>}
          <Badge>{hasMap(cemetery) ? '2D Map: Available' : '2D Map: Coming Soon'}</Badge>
        </div>
        <div className="mt-3 text-xs text-gray-500"><Badge>{cemetery.isOfficial === false ? 'Reference only' : 'Official'}</Badge><p className="mt-2">{cemetery.address || cemetery.notes || '—'}</p><DateTime value={cemetery.updatedAt} /></div>
        <a className="mt-3 inline-block text-sm text-primary underline" href={safeGoogleMapsUrl(cemetery.googleMapsUrl) || 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(cemetery.name + ', ' + cityName)} target="_blank" rel="noreferrer">Open in Google Maps</a>
        <div className="mt-4 flex flex-wrap gap-2">
          <button className="btn-secondary flex-1 text-xs" onClick={onEdit}>Edit</button>
          <button className="btn-secondary flex-1 text-xs" onClick={onToggle}>{cemetery.isActive === false ? 'Activate' : 'Deactivate'}</button>
          <button className="text-xs text-red-600" onClick={onDelete}>Delete</button>
        </div>
      </div>
    </article>
  );
}

function Badge({ children }) {
  return <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-600">{children}</span>;
}

function CityModal({ city, onClose, onSaved }) {
  const [name, setName] = useState(city.name || '');
  const [active, setActive] = useState(city.isActive !== false);
  async function submit(event) {
    event.preventDefault();
    try {
    if (!name.trim()) throw new Error('City name is required.');
    if (city.id && city.isActive !== false && !active && !(await confirmDeactivation('cities', city))) return;
    await checkDuplicate('cities', name, city.id);
    const id = city.approvedFallback || !city.id ? stableCityId(name) : city.id;
    await setDoc(doc(db, 'cities', id), { name: name.trim(), name_az: name.trim(), isOfficial: true, isActive: active, updatedAt: serverTimestamp() }, { merge: true });
    onSaved(city.id ? 'City updated.' : 'City added.', id); onClose();
    } catch (error) { window.alert(error.message || 'Unable to save city.'); }
  }
  return <Modal title={city.id ? 'Edit City' : 'Add City'} onClose={onClose}>
    <form className="space-y-4" onSubmit={submit}>
      <Field label="Azerbaijani city name"><input className="input-field w-full" required value={name} onChange={e => setName(e.target.value)} /></Field>
      <Check label="Active" checked={active} onChange={setActive} />
      <Actions onClose={onClose} />
    </form>
  </Modal>;
}

function CemeteryModal({ cemetery, cities, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: cemetery.name || '', cityId: cemetery.cityId || '', imageUrl: cemetery.imageUrl || '', imagePath: cemetery.imagePath || '',
    googleMapsUrl: cemetery.googleMapsUrl || '', address: cemetery.address || '',
    lat: cemetery.lat ?? cemetery.centerLatitude ?? '', lng: cemetery.lng ?? cemetery.centerLongitude ?? '', notes: cemetery.notes || '', isActive: cemetery.isActive !== false,
    specialCategory: !!cemetery.specialCategory, requiresManualConfirmation: !!cemetery.requiresManualConfirmation,
    graveMapAvailable: !!cemetery.graveMapAvailable,
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(form.imageUrl);
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [saving, setSaving] = useState(false);
  const change = (key, value) => setForm(current => ({ ...current, [key]: value }));

  async function removePhoto() {
    if (!window.confirm('Remove this cemetery thumbnail?')) return;
    change('imageUrl', ''); change('imagePath', '');
    setPhotoRemoved(true); setFile(null); setPreview('');
  }

  async function submit(event) {
    event.preventDefault(); setSaving(true);
    try {
      if (!form.name.trim() || !form.cityId) throw new Error('Cemetery name and city are required.');
      if ((form.lat !== '' && (parseOptionalCoordinate(form.lat) === null || Math.abs(Number(form.lat)) > 90)) || (form.lng !== '' && (parseOptionalCoordinate(form.lng) === null || Math.abs(Number(form.lng)) > 180))) throw new Error('Enter valid latitude/longitude.');
      if (form.googleMapsUrl && !safeGoogleMapsUrl(form.googleMapsUrl)) throw new Error('Enter a valid Google Maps HTTPS link.');
      if (cemetery.id && cemetery.isActive !== false && !form.isActive && !(await confirmDeactivation('cemeteries', cemetery))) return;
      await checkDuplicate('cemeteries', form.name, cemetery.id, form.cityId);
      const city = cities.find(item => item.id === form.cityId);
      const id = cemetery.approvedFallback
        ? stableCemeteryId(city?.name || cemetery.cityName, form.name)
        : cemetery.id || doc(collection(db, 'cemeteries')).id;
      let imageUrl = form.imageUrl; let imagePath = form.imagePath;
      if (file) {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        imagePath = `cemetery-photos/${form.cityId}/${id}/${Date.now()}-${safeName}`;
        const snapshot = await uploadBytes(ref(storage, imagePath), file, { contentType: file.type });
        imageUrl = await getDownloadURL(snapshot.ref);
      }
      await setDoc(doc(db, 'cemeteries', id), {
        name: form.name.trim(), name_az: form.name.trim(), cityId: form.cityId, cityName: city?.name || '', imageUrl, imagePath,
        googleMapsUrl: form.googleMapsUrl.trim(), address: form.address.trim(), isOfficial: true,
        lat: parseOptionalCoordinate(form.lat), lng: parseOptionalCoordinate(form.lng),
        ...(cemetery.centerLatitude != null ? { centerLatitude: parseOptionalCoordinate(form.lat), centerLongitude: parseOptionalCoordinate(form.lng) } : {}), notes: form.notes.trim(),
        isActive: form.isActive, specialCategory: form.specialCategory,
        requiresManualConfirmation: form.requiresManualConfirmation,
        graveMapAvailable: form.graveMapAvailable, updatedAt: serverTimestamp(),
        ...(!cemetery.id ? { createdAt: serverTimestamp() } : {}),
      }, { merge: true });
      const oldImageWasReplaced = file && cemetery.imagePath && cemetery.imagePath !== imagePath;
      if ((photoRemoved || oldImageWasReplaced) && cemetery.imagePath) {
        try { await deleteObject(ref(storage, cemetery.imagePath)); } catch (_) { /* record is already safe */ }
      }
      onSaved(cemetery.id ? 'Cemetery updated.' : 'Cemetery added.'); onClose();
    } catch (error) { window.alert(error.message || 'Unable to save cemetery.'); } finally { setSaving(false); }
  }

  return <Modal title={cemetery.id ? 'Edit Cemetery' : 'Add Cemetery'} onClose={onClose}>
    <form className="space-y-3" onSubmit={submit}>
      <Field label="Azerbaijani cemetery name"><input className="input-field w-full" required value={form.name} onChange={e => change('name', e.target.value)} /></Field>
      <Field label="City"><select className="input-field w-full" required value={form.cityId} onChange={e => change('cityId', e.target.value)}><option value="">Select city</option>{cities.map(city => <option key={city.id} value={city.id}>{city.name}</option>)}</select></Field>
      <div className="grid grid-cols-2 gap-3"><Field label="Latitude"><input className="input-field w-full" type="number" step="any" value={form.lat} onChange={e => change('lat', e.target.value)} /></Field><Field label="Longitude"><input className="input-field w-full" type="number" step="any" value={form.lng} onChange={e => change('lng', e.target.value)} /></Field></div>
      <Field label="Google Maps location"><input className="input-field w-full" type="url" value={form.googleMapsUrl} onChange={e => change('googleMapsUrl', e.target.value)} /></Field>
      <Field label="Address"><input className="input-field w-full" value={form.address} onChange={e => change('address', e.target.value)} /></Field>
      <Field label="Notes"><textarea className="input-field w-full" rows="3" value={form.notes} onChange={e => change('notes', e.target.value)} /></Field>
      <Field label="Cemetery thumbnail">
        {preview && <img src={preview} alt="Preview" className="mb-2 h-36 w-full rounded-xl object-cover" />}
        <input type="file" accept="image/*" onChange={e => { const next = e.target.files?.[0]; setFile(next || null); if (next) setPreview(URL.createObjectURL(next)); }} />
        {(preview || form.imageUrl) && <button type="button" className="ml-2 text-sm text-red-600" onClick={removePhoto}>Remove photo</button>}
      </Field>
      <div className="grid grid-cols-2 gap-2"><Check label="Active" checked={form.isActive} onChange={v => change('isActive', v)} /><Check label="Special" checked={form.specialCategory} onChange={v => change('specialCategory', v)} /><Check label="Manual confirmation" checked={form.requiresManualConfirmation} onChange={v => change('requiresManualConfirmation', v)} /><span className="text-sm">{hasMap(cemetery) ? '2D Map: Available' : '2D Map: Coming Soon'}</span></div>
      <p className="text-xs text-gray-500">Thumbnail uploads use Firebase Storage. Internal grave-map/360 datasets are managed separately.</p>
      <Actions onClose={onClose} saving={saving} />
    </form>
  </Modal>;
}

function Field({ label, children }) { return <label className="block text-xs font-semibold text-gray-600">{label}<div className="mt-1">{children}</div></label>; }
function Check({ label, checked, onChange }) { return <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />{label}</label>; }
function Actions({ onClose, saving }) { return <div className="flex gap-2 pt-2"><button type="button" className="btn-secondary flex-1" onClick={onClose}>Cancel</button><button type="submit" className="btn-primary flex-1" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button></div>; }
function Modal({ title, onClose, children }) { return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={e => e.target === e.currentTarget && onClose()}><div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl"><div className="flex items-center justify-between border-b px-6 py-4"><h2 className="font-bold">{title}</h2><button onClick={onClose}>×</button></div><div className="p-6">{children}</div></div></div>; }
function hasMap(record) { return !!(record.internalMapKey || record.graveMapAvailable); }
async function readCatalogSnapshot() {
  const names = ['cities', 'cemeteries', 'orders', 'users'];
  const snapshots = await Promise.all(names.map(name => getDocs(collection(db, name))));
  const result = Object.fromEntries(names.map((name, i) => [name, snapshots[i].docs.map(d => ({ ...d.data(), id: d.id, data: d.data() }))]));
  return { ...result, references: [...result.orders, ...result.users] };
}
async function confirmDeactivation(collectionName, record) {
  const snapshot = await readCatalogSnapshot();
  const field = collectionName === 'cities' ? 'cityId' : 'cemeteryId';
  const childIds = collectionName === 'cities' ? snapshot.cemeteries.filter(c => c.cityId === record.id).map(c => c.id) : [];
  const count = referenceCount(snapshot.references, field, record.id) + childIds.reduce((sum, id) => sum + referenceCount(snapshot.references, 'cemeteryId', id), 0);
  return window.confirm(`Deactivate “${record.name}”? ${count} existing order/user references will remain intact. It will no longer be available for new selections.`);
}
async function checkDuplicate(collectionName, name, id, cityId) {
  const snapshot = await getDocs(collection(db, collectionName));
  if (snapshot.docs.some(d => d.id !== id && normalizeCatalogName(nameOf(d.data())) === normalizeCatalogName(name) && (!cityId || d.data().cityId === cityId))) {
    throw new Error('A record with this name already exists. Edit or reactivate it instead.');
  }
}
